import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, User, Phone, Mail, BookOpen, CalendarCheck, X, FileText, Upload, Trash2, Hash, Plus, History, Eye, Download, Pencil, KeyRound, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { whatsappUrl } from "@/lib/phone";

const fmt = (iso) => {
  if (!iso) return "—";
  // Iteration 86 — force Gregorian calendar (see Dashboard.jsx for the
  // rationale). Display-only.
  return new Date(iso).toLocaleString("ar-SA-u-ca-gregory", {
    calendar: "gregory",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const STATUS_AR = { pending: "بانتظار الدفع", confirmed: "مؤكدة", canceled: "ملغاة", completed: "منتهية", paid: "مدفوع" };

const useDebounced = (v, ms = 300) => {
  const [out, setOut] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setOut(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return out;
};

const AdminClients = ({ lookup, onLookupHandled }) => {
  const [q, setQ] = useState("");
  const debounced = useDebounced(q, 300);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  // Iteration 67 — manual edit + delete state. Both are explicit admin
  // actions. Nothing is performed automatically.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", middle_name: "", last_name: "", email: "", phone: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Iteration 68 — admin-triggered password reset email.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const sendPasswordReset = async () => {
    if (!detail?.user) return;
    setResetBusy(true);
    try {
      await api.post(`/admin/users/${detail.user.id}/password-reset`);
      toast.success("تم إرسال رابط إعادة تعيين كلمة المرور");
      setResetOpen(false);
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (e.response?.status === 400 && msg?.includes("بريد")) {
        toast.error("لا يوجد بريد إلكتروني لهذا الحساب");
      } else {
        toast.error(msg || "تعذر إرسال رابط إعادة التعيين، حاول مرة أخرى");
      }
    } finally {
      setResetBusy(false);
    }
  };

  const startEdit = () => {
    if (!detail?.user) return;
    setEditForm({
      first_name: detail.user.first_name || "",
      middle_name: detail.user.middle_name || "",
      last_name: detail.user.last_name || "",
      email: detail.user.email || "",
      phone: detail.user.phone || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!detail?.user) return;
    const email = (editForm.email || "").trim();
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("صيغة البريد غير صحيحة");
      return;
    }
    if (!window.confirm("هل تريدين حفظ التعديلات على بيانات هذا الحساب؟")) return;
    setEditSaving(true);
    try {
      const payload = {
        first_name: editForm.first_name.trim() || null,
        middle_name: editForm.middle_name.trim() || null,
        last_name: editForm.last_name.trim() || null,
        email: email || null,
        phone: (editForm.phone || "").trim() || null,
      };
      await api.patch(`/admin/users/${detail.user.id}`, payload);
      toast.success("تم تحديث بيانات الحساب بنجاح");
      setEditOpen(false);
      // Refresh detail + list with new values
      const { data } = await api.get(`/admin/clients/${detail.user.id}`);
      setDetail(data);
      const r = await api.get("/admin/clients", { params: debounced ? { q: debounced } : {} });
      setClients(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر تحديث بيانات الحساب، حاول مرة أخرى");
    } finally {
      setEditSaving(false);
    }
  };
  const confirmDelete = async () => {
    if (!detail?.user) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/users/${detail.user.id}`);
      toast.success("تم حذف الحساب بنجاح");
      setDeleteOpen(false);
      setActive(null);
      setDetail(null);
      const r = await api.get("/admin/clients", { params: debounced ? { q: debounced } : {} });
      setClients(r.data.items || []);
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(msg || "لا يمكن حذف هذا الحساب لأنه مرتبط ببيانات محفوظة.");
      } else {
        toast.error(msg || "تعذر حذف الحساب، حاول مرة أخرى");
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/clients", { params: debounced ? { q: debounced } : {} })
      .then((r) => setClients(r.data.items || []))
      .finally(() => setLoading(false));
  }, [debounced]);

  const open = async (u) => {
    setActive(u);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/clients/${u.id}`);
      setDetail(data);
    } catch {
      setDetail({ user: u, bookings: { upcoming: [], past: [], canceled: [] }, purchases: [] });
    }
  };

  // Iteration 48 — auto-open a client when triggered from another tab
  // (bookings / purchases / tasks). Tries: 1) direct user_id fetch,
  // 2) search by phone, 3) search by email. Shows an Arabic toast if no
  // match is found. Never creates a client.
  useEffect(() => {
    if (!lookup) return;
    let cancelled = false;
    const run = async () => {
      try {
        if (lookup.userId) {
          const { data } = await api.get(`/admin/clients/${lookup.userId}`);
          if (cancelled) return;
          setActive(data.user);
          setDetail(data);
          // Reflect in the list (so the row is highlighted as active).
          setClients((prev) => {
            if (prev.some((c) => c.id === data.user.id)) return prev;
            return [data.user, ...prev];
          });
          return;
        }
        const term = (lookup.phone || lookup.email || "").trim();
        if (!term) {
          toast.error("لم يتم العثور على ملف العميل");
          return;
        }
        setQ(term);  // also updates the search bar UX
        const { data } = await api.get("/admin/clients", { params: { q: term } });
        const match = (data.items || [])[0];
        if (cancelled) return;
        if (match) {
          await open(match);
        } else {
          toast.error("لم يتم العثور على ملف العميل");
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("client lookup failed", e);
        }
        toast.error("لم يتم العثور على ملف العميل");
      } finally {
        if (!cancelled && onLookupHandled) onLookupHandled();
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup]);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Search + list */}
      <div className="card-warm p-5 lg:col-span-1">
        <div className="relative">
          <Search className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5E5C58]" />
          <Input
            data-testid="admin-clients-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بالاسم أو الرقم #١٠٠٢٣ أو الجوال"
            className="pe-10"
            dir="rtl"
          />
        </div>
        <div className="mt-4 space-y-2 max-h-[560px] overflow-auto pe-1" data-testid="admin-clients-list">
          {loading && <p className="text-[#5E5C58] text-sm">جاري التحميل...</p>}
          {!loading && clients.length === 0 && (
            <p className="text-[#5E5C58] text-sm">لا توجد نتائج.</p>
          )}
          {clients.map((c) => (
            <button
              key={c.id}
              data-testid={`admin-client-row-${c.id}`}
              onClick={() => open(c)}
              className={`w-full text-start p-3 rounded-2xl border transition-colors ${
                active?.id === c.id
                  ? "border-[#0A4D3C] bg-[#F6F4EE]"
                  : "border-[#E5E1D8] bg-white hover:bg-[#FAF8F2]"
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-[#222222]">
                <User className="w-4 h-4 text-[#0A4D3C]" />{" "}
                {[c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ") || c.name}
              </div>
              {c.client_number && (
                <div
                  className="mt-1 text-[11px] text-[#A67C41] font-mono inline-flex items-center gap-1"
                  data-testid={`admin-client-number-${c.id}`}
                >
                  <Hash className="w-3 h-3" />#{c.client_number}
                  {c.legacy_client && (
                    <span className="ms-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px]">
                      عميلة قديمة
                    </span>
                  )}
                </div>
              )}
              {c.phone && (() => {
                const wa = whatsappUrl(c.phone);
                return wa ? (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="فتح واتساب"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`admin-client-list-whatsapp-${c.id}`}
                    className="mt-1 text-xs text-[#1ebe5b] hover:underline inline-flex items-center gap-1"
                    dir="ltr"
                  >
                    <MessageCircle className="w-3 h-3" /> {c.phone}
                  </a>
                ) : (
                  <div className="mt-1 text-xs text-[#1ebe5b] inline-flex items-center gap-1" dir="ltr">
                    <Phone className="w-3 h-3" /> {c.phone}
                  </div>
                );
              })()}
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge className="rounded-full bg-stone-100 text-stone-700 text-[10px]">
                  جلسات: {c.bookings_count || 0}
                </Badge>
                <Badge className="rounded-full bg-stone-100 text-stone-700 text-[10px]">
                  كتب: {c.purchases_count || 0}
                </Badge>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="card-warm p-6 lg:col-span-2 min-h-[300px]" data-testid="admin-client-detail">
        {!active && (
          <div className="text-[#5E5C58] text-sm h-full flex items-center justify-center">
            اختر عميل من القائمة لعرض كامل سجلّها.
          </div>
        )}
        {active && !detail && <p className="text-[#5E5C58] text-sm">جاري التحميل...</p>}
        {active && detail && (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-heading text-2xl text-[#222222]">
                    {[detail.user.first_name, detail.user.middle_name, detail.user.last_name].filter(Boolean).join(" ") || detail.user.name}
                  </div>
                  {detail.user.client_number && (
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#A67C41]/10 text-[#A67C41] font-mono text-sm"
                      data-testid="admin-client-detail-number"
                    >
                      <Hash className="w-3.5 h-3.5" />#{detail.user.client_number}
                    </span>
                  )}
                </div>
                <div className="mt-2 text-sm text-[#5E5C58] flex flex-wrap gap-x-4 gap-y-1">
                  {detail.user.email && (
                    <span className="inline-flex items-center gap-1" dir="ltr">
                      <Mail className="w-3.5 h-3.5" /> {detail.user.email}
                    </span>
                  )}
                  {detail.user.phone && (() => {
                    const wa = whatsappUrl(detail.user.phone);
                    return wa ? (
                      <a
                        href={wa}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="فتح واتساب"
                        data-testid="admin-client-detail-whatsapp"
                        className="inline-flex items-center gap-1 text-[#1ebe5b] hover:underline"
                        dir="ltr"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> {detail.user.phone}
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#1ebe5b]" dir="ltr">
                        <Phone className="w-3.5 h-3.5" /> {detail.user.phone}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="admin-client-edit-btn"
                  variant="outline"
                  size="icon"
                  className="rounded-full text-[#0A4D3C] border-[#0A4D3C]/40 hover:bg-[#F6F4EE]"
                  title="تعديل بيانات الحساب"
                  onClick={startEdit}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="admin-client-password-reset-btn"
                  variant="outline"
                  size="icon"
                  className="rounded-full text-[#8C6239] border-[#8C6239]/40 hover:bg-[#F6F4EE]"
                  title="إرسال رابط إعادة تعيين كلمة المرور"
                  onClick={() => setResetOpen(true)}
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="admin-client-delete-btn"
                  variant="outline"
                  size="icon"
                  className="rounded-full text-[#B33939] border-[#B33939]/40 hover:bg-rose-50"
                  title="حذف الحساب"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="admin-client-close"
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                  onClick={() => {
                    setActive(null);
                    setDetail(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {detail.user.legacy_client && (
              <div
                className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900"
                data-testid="admin-client-legacy-card"
              >
                <div className="flex items-center gap-2 font-medium">
                  <History className="w-4 h-4" /> عميلة قديمة
                </div>
                <div className="mt-1 text-xs text-amber-800">
                  أول جلسة سابقة (تقريبًا):{" "}
                  <strong className="font-mono" dir="ltr">
                    {detail.user.legacy_first_session_date || "غير محدّد"}
                  </strong>
                </div>
              </div>
            )}

            <Section title="الجلسات القادمة" items={detail.bookings.upcoming} icon={CalendarCheck} kind="booking" />
            <Section title="الجلسات السابقة" items={detail.bookings.past} icon={CalendarCheck} kind="booking" />
            <Section title="الجلسات الملغاة" items={detail.bookings.canceled} icon={CalendarCheck} kind="booking" />
            <Section title="الكتب المُشتراة" items={detail.purchases} icon={BookOpen} kind="purchase" />

            <AdminNotesHistory
              userId={detail.user.id}
              initialEntries={detail.notes_entries || []}
            />

            <AttachmentsManager
              userId={detail.user.id}
              initialItems={detail.attachments || []}
            />
          </>
        )}
      </div>

      {/* Iteration 67 — Edit-user modal */}
      {editOpen && detail?.user && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          data-testid="admin-client-edit-modal"
          onClick={() => !editSaving && setEditOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="font-heading text-xl text-[#222222]">تعديل بيانات الحساب</div>
            <div className="text-xs text-[#5E5C58] mt-1">
              لن يتم تعديل سجل الحجوزات أو المشتريات أو الملاحظات.
            </div>
            <div className="mt-5 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs text-[#5E5C58]">
                  الاسم الأول
                  <Input
                    data-testid="admin-edit-first-name"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="mt-1"
                  />
                </label>
                <label className="text-xs text-[#5E5C58]">
                  الاسم الأوسط
                  <Input
                    data-testid="admin-edit-middle-name"
                    value={editForm.middle_name}
                    onChange={(e) => setEditForm({ ...editForm, middle_name: e.target.value })}
                    className="mt-1"
                  />
                </label>
                <label className="text-xs text-[#5E5C58]">
                  الاسم الأخير
                  <Input
                    data-testid="admin-edit-last-name"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="mt-1"
                  />
                </label>
              </div>
              <label className="text-xs text-[#5E5C58] block">
                البريد الإلكتروني
                <Input
                  data-testid="admin-edit-email"
                  type="email"
                  dir="ltr"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1"
                />
              </label>
              <label className="text-xs text-[#5E5C58] block">
                رقم الجوال / Botim
                <Input
                  data-testid="admin-edit-phone"
                  dir="ltr"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="mt-1"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                data-testid="admin-edit-cancel"
              >
                إلغاء
              </Button>
              <Button
                onClick={saveEdit}
                disabled={editSaving}
                className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
                data-testid="admin-edit-save"
              >
                {editSaving ? "جارٍ الحفظ..." : "حفظ التعديلات"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Iteration 67 — Delete-user confirmation modal */}
      {deleteOpen && detail?.user && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          data-testid="admin-client-delete-modal"
          onClick={() => !deleteBusy && setDeleteOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="font-heading text-xl text-[#B33939]">حذف الحساب</div>
            <p className="text-sm text-[#222222] mt-3 leading-relaxed">
              هل أنت متأكد من حذف هذا الحساب؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
            <p className="text-xs text-[#5E5C58] mt-2">
              سيُرفض الحذف إذا كان للحساب أي حجوزات أو مشتريات أو ملاحظات أو مرفقات.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteBusy}
                data-testid="admin-delete-cancel"
              >
                إلغاء
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleteBusy}
                className="rounded-full bg-[#B33939] hover:bg-[#8f2a2a] text-white"
                data-testid="admin-delete-confirm"
              >
                {deleteBusy ? "جارٍ الحذف..." : "نعم، احذف الحساب"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Iteration 68 — Admin password reset email confirmation modal */}
      {resetOpen && detail?.user && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          data-testid="admin-client-reset-modal"
          onClick={() => !resetBusy && setResetOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="font-heading text-xl text-[#8C6239]">إرسال رابط إعادة تعيين كلمة المرور</div>
            <p className="text-sm text-[#222222] mt-3 leading-relaxed">
              هل تريدين إرسال رابط إعادة تعيين كلمة المرور لهذا الحساب؟
            </p>
            <p className="text-xs text-[#5E5C58] mt-2" dir="ltr">
              {detail.user.email || "—"}
            </p>
            <p className="text-xs text-[#5E5C58] mt-2">
              الرابط آمن ومُوقَّع، صالح لمدة 30 دقيقة فقط ولن يستخدمه إلا صاحب الحساب.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setResetOpen(false)}
                disabled={resetBusy}
                data-testid="admin-reset-cancel"
              >
                إلغاء
              </Button>
              <Button
                onClick={sendPasswordReset}
                disabled={resetBusy}
                className="rounded-full bg-[#8C6239] hover:bg-[#6f4d2c] text-white"
                data-testid="admin-reset-confirm"
              >
                {resetBusy ? "جارٍ الإرسال..." : "نعم، أرسلي الرابط"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, items, icon: Icon, kind }) => (
  <div className="mt-6">
    <h4 className="text-sm font-medium text-[#5E5C58] flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#0A4D3C]" /> {title}{" "}
      <span className="text-xs text-[#A39C8E]">({items.length})</span>
    </h4>
    <div className="mt-2 space-y-2">
      {items.length === 0 && <p className="text-xs text-[#A39C8E]">—</p>}
      {items.map((it) => (
        <div
          key={it.id}
          className="rounded-xl border border-[#E5E1D8] bg-white p-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div>
            <div className="font-medium text-[#222222]">
              {kind === "booking" ? it.service_title_ar : it.product_title_ar}
            </div>
            <div className="text-xs text-[#5E5C58] mt-0.5">
              {fmt(kind === "booking" ? it.slot_iso : it.created_at)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="rounded-full bg-stone-100 text-stone-700">
              {STATUS_AR[it.status] || it.status}
            </Badge>
            <span className="text-[#0A4D3C] font-heading text-sm">
              {Number(it.amount || 0).toLocaleString("ar-SA")} ر.س
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default AdminClients;


// ---------------------------------------------------------------------------
// Iter-47 sub-components
// ---------------------------------------------------------------------------
const AdminNotesHistory = ({ userId, initialEntries }) => {
  const [entries, setEntries] = useState(initialEntries);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const pendingTimerRef = useRef(null);

  useEffect(() => {
    setEntries(initialEntries);
    setDraft("");
    setPendingDeleteId(null);
  }, [userId, initialEntries]);

  // Clean up any pending-confirm timer on unmount.
  useEffect(() => () => {
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
  }, []);

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/clients/${userId}/notes`, { text });
      setEntries((prev) => [data.entry, ...prev]);
      setDraft("");
      toast.success("تمت إضافة الملاحظة");
    } catch (err) {
      console.error("admin note add failed", err);
      toast.error(err?.response?.data?.detail || "تعذّر حفظ الملاحظة");
    } finally {
      setSaving(false);
    }
  };

  // Two-click delete: first click arms the button (turns red + label "تأكيد")
  // for 3 seconds; second click within that window commits the deletion.
  // Replaces the unreliable native window.confirm() dialog.
  const armOrDelete = async (entry) => {
    if (pendingDeleteId !== entry.id) {
      setPendingDeleteId(entry.id);
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = setTimeout(() => setPendingDeleteId(null), 3000);
      return;
    }
    // Confirmed — commit deletion
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    setPendingDeleteId(null);
    try {
      await api.delete(`/admin/clients/${userId}/notes/${entry.id}`);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success("تم حذف الملاحظة");
    } catch (err) {
      console.error("admin note delete failed", err);
      toast.error("تعذّر الحذف");
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#E5E1D8] bg-[#FBF9F4] p-4" data-testid="admin-notes-history">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-[#5E5C58] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#0A4D3C]" /> سجل ملاحظات المستشارة
          <span className="text-[10px] text-[#A39C8E]">(غير ظاهرة للعميلة)</span>
        </h4>
        <span className="text-[10px] text-[#A39C8E]" data-testid="admin-notes-count">
          {entries.length} ملاحظة
        </span>
      </div>

      <textarea
        data-testid="admin-notes-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        maxLength={20000}
        className="w-full rounded-lg border border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A4D3C]"
        placeholder="اكتبي ملاحظة جديدة... (تُضاف للسجل ولا تستبدل القديمة)"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-[#A39C8E]">{draft.length} / 20000 حرف</span>
        <Button
          data-testid="admin-notes-add-btn"
          onClick={add}
          disabled={saving || !draft.trim()}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-8 px-4 text-xs"
        >
          <Plus className="w-3 h-3 ms-1" />
          {saving ? "جارٍ الإضافة..." : "إضافة ملاحظة"}
        </Button>
      </div>

      <div className="mt-4 space-y-2" data-testid="admin-notes-list">
        {entries.length === 0 && (
          <p className="text-xs text-[#A39C8E]">لا توجد ملاحظات سابقة.</p>
        )}
        {entries.map((e) => {
          const armed = pendingDeleteId === e.id;
          return (
            <div
              key={e.id}
              data-testid={`admin-note-entry-${e.id}`}
              className="rounded-lg border border-[#E5E1D8] bg-white p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-[#A39C8E] flex items-center gap-2">
                  <History className="w-3 h-3" />
                  <span>{fmt(e.created_at)}</span>
                  {e.created_by_name && (
                    <span className="text-[#5E5C58]">— {e.created_by_name}</span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`admin-note-delete-${e.id}`}
                  onClick={() => armOrDelete(e)}
                  title={armed ? "اضغطي مرة أخرى للتأكيد" : "حذف"}
                  className={
                    armed
                      ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[11px] font-medium animate-pulse"
                      : "text-rose-500 hover:text-rose-700 transition-colors"
                  }
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {armed && <span>تأكيد الحذف</span>}
                </button>
              </div>
              <p className="mt-1.5 text-sm text-[#222222] whitespace-pre-wrap break-words">
                {e.text}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MAX_UPLOAD_MB = 10;
const ALLOWED_EXTS = ["pdf", "png", "jpg", "jpeg", "webp", "docx", "txt"];
const PREVIEWABLE_MIMES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

const AttachmentsManager = ({ userId, initialItems }) => {
  const [items, setItems] = useState(initialItems);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [busyId, setBusyId] = useState(null);   // attachment id currently downloading/previewing
  const [preview, setPreview] = useState(null);  // { url, mime, filename }
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const pendingTimerRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setItems(initialItems);
    setPendingDeleteId(null);
  }, [userId, initialItems]);

  // Cleanup pending-delete timer on unmount
  useEffect(() => () => {
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
  }, []);

  // Revoke blob URLs when the preview closes / component unmounts
  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`نوع غير مسموح: .${ext}`);
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`الحجم يتجاوز ${MAX_UPLOAD_MB} ميجابايت`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (label.trim()) fd.append("label", label.trim());
      const { data } = await api.post(`/admin/clients/${userId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data.attachment, ...prev]);
      setLabel("");
      toast.success(`تم رفع: ${file.name}`);
    } catch (err) {
      console.error("attachment upload failed", err);
      toast.error(err?.response?.data?.detail || "فشل رفع الملف");
    } finally {
      setUploading(false);
    }
  };

  // Two-click delete (same pattern as notes): first click arms for 3s, second commits.
  const armOrDelete = async (att) => {
    if (pendingDeleteId !== att.id) {
      setPendingDeleteId(att.id);
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = setTimeout(() => setPendingDeleteId(null), 3000);
      return;
    }
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    setPendingDeleteId(null);
    try {
      await api.delete(`/admin/clients/${userId}/attachments/${att.id}`);
      setItems((prev) => prev.filter((x) => x.id !== att.id));
      toast.success("تم حذف الملف");
    } catch (err) {
      console.error("attachment delete failed", err);
      toast.error("تعذّر الحذف");
    }
  };

  // Fetch attachment as Blob with JWT header — works regardless of how the
  // backend authenticates because the axios instance attaches the token.
  const fetchBlob = async (att) => {
    const res = await api.get(
      `/admin/clients/${userId}/attachments/${att.id}/download`,
      { responseType: "blob" }
    );
    // axios on blob responses doesn't auto-parse content-type, use the header.
    const mime = res.headers["content-type"] || att.content_type || att.mime || "application/octet-stream";
    return { blob: res.data, mime };
  };

  const downloadFile = async (att) => {
    setBusyId(att.id);
    try {
      const { blob } = await fetchBlob(att);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Free memory after the browser has had a chance to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("download failed", err);
      toast.error("تعذّر تحميل الملف");
    } finally {
      setBusyId(null);
    }
  };

  const previewFile = async (att) => {
    setBusyId(att.id);
    try {
      const { blob, mime } = await fetchBlob(att);
      const url = URL.createObjectURL(blob);
      setPreview({ url, mime, filename: att.filename });
    } catch (err) {
      console.error("preview failed", err);
      toast.error("تعذّر فتح المعاينة");
    } finally {
      setBusyId(null);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const canPreview = (att) => PREVIEWABLE_MIMES.includes(att.content_type || att.mime);

  return (
    <div className="mt-6" data-testid="admin-attachments">
      <h4 className="text-sm font-medium text-[#5E5C58] flex items-center gap-2 mb-2">
        <Upload className="w-4 h-4 text-[#0A4D3C]" /> المرفقات
        <span className="text-[10px] text-[#A39C8E]">({items.length})</span>
      </h4>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input
          data-testid="admin-attachment-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="وصف اختياري للملف"
          className="flex-1 min-w-[200px] h-9"
        />
        <input
          ref={fileRef}
          type="file"
          accept={ALLOWED_EXTS.map((e) => `.${e}`).join(",")}
          onChange={onPick}
          className="hidden"
          data-testid="admin-attachment-file-input"
        />
        <Button
          data-testid="admin-attachment-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-full bg-[#A67C41] hover:bg-[#8C6239] text-white h-9 px-4 text-xs"
        >
          <Upload className="w-3 h-3 ms-1" />
          {uploading ? "جاري الرفع..." : "رفع ملف"}
        </Button>
      </div>
      <p className="text-[10px] text-[#A39C8E] mb-3">
        الأنواع المسموحة: {ALLOWED_EXTS.join("، ")} — الحدّ الأقصى {MAX_UPLOAD_MB} ميجابايت
      </p>

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="text-xs text-[#A39C8E]">لا توجد مرفقات بعد.</p>
        )}
        {items.map((att) => (
          <div
            key={att.id}
            data-testid={`admin-attachment-row-${att.id}`}
            className="rounded-xl border border-[#E5E1D8] bg-white p-3 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[#222222] truncate" data-testid={`admin-attachment-name-${att.id}`}>
                {att.filename}
              </div>
              <div className="text-xs text-[#5E5C58] flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
                {att.label && <span>{att.label}</span>}
                <span>{fmt(att.uploaded_at)}</span>
                <span dir="ltr">{(att.size_bytes / 1024).toFixed(1)} KB</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canPreview(att) && (
                <Button
                  data-testid={`admin-attachment-preview-${att.id}`}
                  variant="outline"
                  size="icon"
                  className="rounded-full text-[#0A4D3C] border-[#0A4D3C]/30 hover:bg-[#0A4D3C]/5 h-8 w-8"
                  onClick={() => previewFile(att)}
                  disabled={busyId === att.id}
                  title="معاينة"
                >
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                data-testid={`admin-attachment-download-${att.id}`}
                variant="outline"
                size="icon"
                className="rounded-full text-[#A67C41] border-[#A67C41]/30 hover:bg-[#A67C41]/5 h-8 w-8"
                onClick={() => downloadFile(att)}
                disabled={busyId === att.id}
                title="تحميل"
              >
                <Download className="w-3.5 h-3.5" />
              </Button>
              {(() => {
                const armed = pendingDeleteId === att.id;
                return (
                  <Button
                    type="button"
                    data-testid={`admin-attachment-delete-${att.id}`}
                    variant={armed ? "default" : "outline"}
                    size={armed ? "default" : "icon"}
                    className={
                      armed
                        ? "rounded-full bg-rose-600 hover:bg-rose-700 text-white h-8 px-3 text-[11px] animate-pulse"
                        : "rounded-full text-rose-600 border-rose-200 hover:bg-rose-50 h-8 w-8"
                    }
                    onClick={() => armOrDelete(att)}
                    title={armed ? "اضغطي مرة أخرى للتأكيد" : "حذف"}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {armed && <span className="ms-1">تأكيد الحذف</span>}
                  </Button>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <AttachmentPreviewModal
          preview={preview}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

const AttachmentPreviewModal = ({ preview, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isImage = preview.mime.startsWith("image/");
  const isPdf = preview.mime === "application/pdf";

  return (
    <div
      data-testid="admin-attachment-preview-modal"
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E1D8] bg-[#FBF9F4]">
          <div className="font-medium text-[#222222] truncate" data-testid="admin-attachment-preview-filename">
            {preview.filename}
          </div>
          <Button
            data-testid="admin-attachment-preview-close"
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 bg-[#1f1f1f] flex items-center justify-center overflow-auto">
          {isImage && (
            <img
              src={preview.url}
              alt={preview.filename}
              className="max-w-full max-h-full object-contain"
              data-testid="admin-attachment-preview-image"
            />
          )}
          {isPdf && (
            <iframe
              src={preview.url}
              title={preview.filename}
              className="w-full h-full bg-white"
              data-testid="admin-attachment-preview-pdf"
            />
          )}
          {!isImage && !isPdf && (
            <p className="text-white text-sm">المعاينة غير متاحة لهذا النوع.</p>
          )}
        </div>
      </div>
    </div>
  );
};
